import { useMemo, useState } from "react";
import { Plus, Pencil, CheckCircle2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useApi, useResource, errorText } from "@/lib/api";
import { MONTH_NAMES, formatMoney, formatAUD, sumDecimals } from "@/lib/format";
import FyTabs from "@/components/FyTabs";
import RecordDialog from "@/components/RecordDialog";
import ConfirmDelete from "@/components/ConfirmDelete";

const FY_MONTHS = [7, 8, 9, 10, 11, 12, 1, 2, 3, 4, 5, 6];

export default function ForecastPage() {
  const api = useApi();
  const { data: fys } = useResource("/financial_years");
  const { data: methods } = useResource("/payment_methods");
  const [fy, setFy] = useState(null);
  const { data: items, reload } = useResource(fy ? `/forecast_items?fy=${fy}` : null);
  const [editing, setEditing] = useState(null);

  const fields = useMemo(() => [
    { key: "month", label: "Month", type: "select", options: FY_MONTHS.map((m) => ({ value: String(m), label: MONTH_NAMES[m - 1] })) },
    { key: "name", label: "Name", type: "text" },
    { key: "payment_method", label: "Payment method", type: "select",
      options: (methods || []).filter((m) => m.active).map((m) => ({ value: m.id, label: m.name })) },
    { key: "amount", label: "Amount", type: "decimal" },
  ], [methods]);
  const methodName = (id) => (methods || []).find((m) => m.id === id)?.name || "—";
  const rows = [...(items || [])].sort((a, b) => FY_MONTHS.indexOf(a.month) - FY_MONTHS.indexOf(b.month));

  const save = async (form) => {
    const body = { fy, month: Number(form.month), name: form.name, payment_method: form.payment_method, amount: form.amount, status: form.status || "expected" };
    try {
      if (editing.id) await api("put", `/forecast_items/${editing.id}`, body);
      else await api("post", "/forecast_items", body);
      toast.success("Forecast saved");
      setEditing(null);
      reload();
    } catch (e) { return errorText(e); }
  };
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errorText(e)); } };
  const setStatus = (r, status) => run(() => api("put", `/forecast_items/${r.id}`, { status }), status === "received" ? `${r.name} marked received` : `${r.name} back to expected`);

  return (
    <div className="rise">
      <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="forecast-title">Forecast</h1>
      <p className="mt-3 text-muted-foreground text-base md:text-lg">Expected payments by month. Mark one as received when the real payment arrives.</p>
      <div className="mt-8 flex flex-wrap items-center gap-4">
        <FyTabs fys={fys} value={fy} onChange={setFy} prefix="forecast" />
        <Button className="ml-auto" disabled={!fy} data-testid="add-forecast-button"
          onClick={() => setEditing({ month: "7", name: "", payment_method: methods?.[0]?.id || "", amount: "" })}>
          <Plus className="h-4 w-4 mr-1" /> Add forecast
        </Button>
      </div>
      <div className="mt-6 grid grid-cols-3 gap-4 max-w-2xl">
        {[["Total forecast", rows], ["Expected", rows.filter((r) => r.status === "expected")], ["Received", rows.filter((r) => r.status === "received")]].map(([l, list]) => (
          <div key={l} className="border border-border bg-card p-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{l}</div>
            <div className="font-mono text-lg mt-1" data-testid={`forecast-stat-${l.split(" ").pop().toLowerCase()}`}>{formatAUD(sumDecimals(list.map((r) => r.amount)))}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="forecast-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              {["Month", "Name", "Method", "Amount", "Status", ""].map((h) => <th key={h} className={`px-5 py-3 font-medium ${h === "Amount" ? "text-right" : ""}`}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className={`border-b border-border hover:bg-accent/30 transition-colors ${r.status === "received" ? "opacity-60" : ""}`} data-testid={`forecast-row-${r.id}`}>
                <td className="px-5 py-3 font-mono text-xs">{MONTH_NAMES[r.month - 1]}</td>
                <td className="px-5 py-3">{r.name}</td>
                <td className="px-5 py-3"><span className="text-xs border border-border px-2 py-0.5">{methodName(r.payment_method)}</span></td>
                <td className="px-5 py-3 text-right font-mono">{formatMoney(r.amount)}</td>
                <td className="px-5 py-3">
                  <span className={`text-xs font-mono uppercase ${r.status === "received" ? "text-emerald-300" : "text-amber-300"}`} data-testid={`forecast-status-${r.id}`}>{r.status}</span>
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  {r.status === "expected" ? (
                    <Button size="sm" variant="outline" onClick={() => setStatus(r, "received")} data-testid={`forecast-mark-received-${r.id}`}>
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Mark received
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => setStatus(r, "expected")} data-testid={`forecast-mark-expected-${r.id}`}>
                      <Undo2 className="h-4 w-4 mr-1" /> Undo
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing({ ...r, month: String(r.month) })} data-testid={`forecast-edit-${r.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <ConfirmDelete name={r.name} testId={`forecast-delete-${r.id}`} onConfirm={() => run(() => api("delete", `/forecast_items/${r.id}`), "Forecast deleted")} />
                </td>
              </tr>
            ))}
            {items && rows.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-muted-foreground">No forecast items for {fy}.</td></tr>}
          </tbody>
        </table>
      </div>
      <RecordDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} prefix="forecast-form"
        title={editing?.id ? "Edit forecast" : `Add forecast for ${fy}`} fields={fields} initial={editing || {}} onSubmit={save} />
    </div>
  );
}
